import React from 'react';
import {
  AfterCellChanged,
  GenerateCellParams,
  GridType,
  ResetParams,
  RowFormat
} from 'common/types/scalarSpreadsheet/scalarSpreadsheet';
import { FieldAttributeModelMap } from 'common/types/store';
import { DEFAULT_CURRENCY, DEFAULT_CURRENCY_SYMBOL } from 'components/FeaturedSpreadsheet/constants';
import { GridRowLabel } from 'components/Grid';
import { Cell } from 'components/ScalarSpreadsheet/utilities/Cell';
import {
  ConditionsParams,
  ConfigurationCell,
  ConfigurationCellMap,
  CustomValidationParams,
  EvaluatedCell,
  EvaluatedCellMap,
  ICell,
  ParserParams, ReverseParserParams,
  ScalarSpreadsheetConfigDataInterface, TParserParams
} from 'models/interfaces/spreadsheet-types/core-types';
import { CellChange } from 'models/interfaces/spreadsheet-types/workbook-types';
import { mergeCells, removeClassName } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import logger from 'utilities/logger';
import defaultTitleParser from './defaultTitleParser';

const getLegendRowLayoutStyle = (style?: React.CSSProperties | null): React.CSSProperties => ({
  ...(style?.display ? { display: style.display } : {}),
  ...(style?.height ? { height: style.height } : {}),
  ...(style?.minHeight ? { minHeight: style.minHeight } : {}),
  ...(style?.maxHeight ? { maxHeight: style.maxHeight } : {}),
});

const getRowLegendValue = (row: ICell, rowIndex: number): string | number => {
  const xlsxRow = row as ICell & { isXlsxPaddingRow?: boolean; isXlsxPreviewRow?: boolean };

  if (xlsxRow.isXlsxPaddingRow || (xlsxRow.isXlsxPreviewRow && row.rowNumber === 0)) {
    return '';
  }

  if (xlsxRow.isXlsxPreviewRow && row.rowNumber != null) {
    return row.rowNumber;
  }

  return rowIndex + 1;
};

export interface SpreadsheetConfigConstructorParams<I extends object, COLUMN extends object, TD extends object, CD extends object> extends ScalarSpreadsheetConfigDataInterface<I, COLUMN, TD, CD> {
    columns: any[];
    parser: (params: ParserParams<COLUMN, I, TD, CD>) => Promise<ConfigurationCellMap<CD, I, COLUMN, TD>> | ConfigurationCellMap<CD, I, COLUMN, TD>;
    totalParser?: (params: any) => any;
    titleParser?: (params: TParserParams<I, COLUMN, TD, CD>) => ConfigurationCellMap<CD, I, COLUMN, TD>;
    rowConfig: ICell<CD, I, COLUMN, TD>[];
    name: string;
    tableData: any;
    getColumns?: () => any[] | undefined;
    afterCellChanged: AfterCellChanged;
    conditions?: (params: ConditionsParams<I, COLUMN, TD, CD>) => void;
    disabled?: boolean;
    currency?: string;
    ignoreCurrencyChangeNumber?: number;
    hasColTitle?: boolean;
    colTitleRow?: number;
    reverseParser: ScalarSpreadsheetConfigDataInterface<I, COLUMN, TD, CD>['reverseParser'];
    setDataSource?: (params: any) => void;
    tableTerms?: any;
    customTitles?: any[];
    setColumns?: (columns: any[]) => void;
    linkCurrencyChips?: boolean;
    showTotalColumn?: boolean;
    showTitlesColumn?: boolean;
    showToolbar?: boolean;
    showPreviousColsDivider?: boolean;
    showFirstRowAsTitle?: boolean;
    allowConfirmAndDeleteColumn?: boolean;
    matchCurrencyPadding?: boolean;
    handleDeleteColumn?: () => void;
    allowCloneColumn?: boolean;
    cloneColumn?: () => void;
    allowReorderColumns?: boolean;
    allowEmptyValues?: boolean;
    page?: string;
    currencyFormatter?: any;
    unitsFormatter?: any;
    format?: RowFormat;
    initialObj?: I;
    customValidations?: any;
    shouldValidate?: boolean;
    fieldAttributes?: Record<string, any>;
    allowDeleteOnlySpecificColumn?: boolean;
    disableDeleteBtnOnFirstColumn?: boolean;
    customDeleteConfirmation?: boolean;
    alwaysDisplayLegend?: boolean;
    reverseParseAfterLoad?: boolean;
    isReadOnly?: boolean;
    displayRowIndicator?: boolean;
    displayColumnIndicator?: boolean;
    allowCopyColumn?: boolean;
    allowDeleteColumn?: boolean;
    allowCopyRows?: boolean;
    allowAddMultipleRows?: boolean;
    allowAddMultipleColumns?: boolean;
    allowAddSingleRow?: boolean;
    allowDeleteRow?: boolean;
    alphabet?: string[];
    ignoreCellExpressionErrors: boolean
  }

export class SpreadsheetConfig<I extends object, COLUMN extends object, TD extends object, CD extends object = object> implements ScalarSpreadsheetConfigDataInterface<I, COLUMN, TD, CD> {
  private readonly alwaysDisplayLegend?: boolean;

  public readonly ignoreCellExpressionErrors?: boolean;

  private readonly cloneColumn?: () => void;

  private readonly colTitleRow: number;

  private readonly compareChanges: () => void;

  private readonly currencyFormatter?: any;

  private readonly customDeleteConfirmation?: boolean;

  private readonly customTitles?: any[];

  private readonly deleteColumn: () => void;

  private readonly disableDeleteBtnOnFirstColumn?: boolean;

  private readonly displayColumnIndicator: boolean;

  private readonly displayRowIndicator: boolean;

  private readonly format?: RowFormat;

  private readonly getColumns: () => (COLUMN[] | undefined);

  private readonly handleDeleteColumn?: () => void;

  private readonly ignoreCurrencyChangeNumber?: number;

  private readonly initialCurrencyType?: string;

  private readonly initialTableData: object;

  private readonly isReadOnly?: boolean;

  private readonly linkCurrencyChips: boolean;

  private readonly matchCurrencyPadding: boolean;

  private readonly page?: string;

  private readonly reverseParseAfterLoad?: boolean;

  private readonly setColumns?: (columns: any[]) => void;

  private readonly setDataSource?: (params: any) => void;

  private readonly showFirstRowAsTitle: boolean;

  private readonly showPreviousColsDivider: boolean;

  private readonly showToolbar: boolean;

  private readonly tableTerms?: any;

  private readonly unitsFormatter?: any;

  public shouldValidate?: boolean;

  private xLength: number;

  private yLength: number;

  afterCellChanged: AfterCellChanged;

  allowAddMultipleColumns?: boolean;

  allowAddMultipleRows?: boolean;

  allowAddSingleRow?: boolean;

  allowCloneColumn?: boolean;

  allowConfirmAndDeleteColumn?: boolean;

  allowCopyColumn?: boolean;

  allowCopyRows?: boolean;

  allowDeleteColumn?: boolean;

  allowDeleteOnlySpecificColumn?: boolean;

  allowDeleteRow?: boolean;

  allowEmptyValues?: boolean;

  allowReorderColumns?: boolean;

  allowSortColumns?: boolean;

  cells: ConfigurationCellMap<CD, I, COLUMN, TD> | undefined;

  children?: React.JSX.Element;

  className?: string;

  collapsibleColumns: { [key: string]: any };

  columns: COLUMN[];

  conditions?: ((params: ConditionsParams<I, COLUMN, TD, CD>) => void);

  currency?: string;

  customValidations: ((params: CustomValidationParams<I, COLUMN, TD, CD>) => void) | undefined;

  data: EvaluatedCell<CD, I, COLUMN, TD>[][] | undefined;

  deleteRowFn: (rowNumber: number) => void;

  disabled?: boolean;

  disableVerticalScroll: boolean;

  displayDeleteRowBtn: boolean;

  fieldAttributes?: FieldAttributeModelMap;

  formatDispatch: (args: any) => void;

  hasColTitle?: boolean;

  get stripeRowConfig() {
    return {
      hasColTitle: this.hasColTitle,
      colTitleRow: this.colTitleRow,
      showFirstRowAsTitle: this.showFirstRowAsTitle,
    };
  }

  id: string;

  initialObj?: I;

  isLedgerTable: boolean;

  isToolbarVisible: boolean;

  name: string;

  notShowingQuarters: boolean;

  parser: (params: ParserParams<COLUMN, I, TD, CD>) => (Promise<ConfigurationCellMap<CD, I, COLUMN, TD>> | ConfigurationCellMap<CD, I, COLUMN, TD>);

  reverseParser: ((reverseParserParams: ReverseParserParams<I, COLUMN, TD, CD>) => (void | I)) | undefined;

  rowConfig: ICell<CD, I, COLUMN, TD>[];

  showPlaceholder: boolean;

  showTitlesColumn?: boolean;

  showTotalColumn?: boolean;

  tableData: TD;

  titleCells: ConfigurationCellMap<CD, I, COLUMN, TD> | undefined;

  titleData: EvaluatedCell<CD, I, COLUMN, TD>[][] | undefined;

  titleParser: (params: TParserParams<I, COLUMN, TD, CD>) => ConfigurationCellMap<CD, I, COLUMN, TD>;

  totalCells: ConfigurationCellMap<CD, I, COLUMN, TD> | undefined;

  alphabet: string[] | undefined;

  totalData: EvaluatedCell<CD, I, COLUMN, TD>[][] | undefined;

  totalParser?: (params: TParserParams<I, COLUMN, TD, CD>) => ConfigurationCellMap<CD, I, COLUMN, TD>;

  validatedTitleCells?: EvaluatedCellMap<CD, I, COLUMN, TD>;

  constructor({
    columns,
    parser,
    totalParser,
    titleParser = defaultTitleParser,
    rowConfig,
    name,
    alphabet,
    tableData,
    getColumns = () => undefined,
    afterCellChanged = ((changes, _1, _2, _3) => changes),
    conditions = undefined,
    disabled = false,
    currency = `${DEFAULT_CURRENCY} ${DEFAULT_CURRENCY_SYMBOL}`,
    ignoreCurrencyChangeNumber = 0,
    hasColTitle = true,
    colTitleRow = 0,
    reverseParser = undefined,
    setDataSource = undefined,
    tableTerms = undefined,
    customTitles = undefined,
    setColumns = undefined,
    linkCurrencyChips = false,
    showTotalColumn = false,
    showTitlesColumn = true,
    showToolbar = false,
    showPreviousColsDivider = false,
    showFirstRowAsTitle = true,
    allowConfirmAndDeleteColumn = true,
    matchCurrencyPadding = false,
    handleDeleteColumn = undefined,
    allowCloneColumn = undefined,
    cloneColumn = undefined,
    allowReorderColumns = false,
    allowEmptyValues = true,
    page = undefined,
    currencyFormatter,
    unitsFormatter,
    format,
    initialObj = undefined,
    customValidations = undefined,
    shouldValidate = true,
    fieldAttributes = {},
    allowDeleteOnlySpecificColumn = false,
    disableDeleteBtnOnFirstColumn = false,
    customDeleteConfirmation = false,
    alwaysDisplayLegend = false,
    reverseParseAfterLoad = false,
    isReadOnly = false,
    displayRowIndicator = true,
    displayColumnIndicator = true,
    allowCopyColumn = false,
    allowDeleteColumn = false,
    allowCopyRows = false,
    allowAddMultipleRows = false,
    allowAddMultipleColumns = false,
    allowAddSingleRow = false,
    allowDeleteRow = false,
    ignoreCellExpressionErrors = false,
  }: SpreadsheetConfigConstructorParams<I, COLUMN, TD, CD>) {
    this.ignoreCellExpressionErrors = ignoreCellExpressionErrors
    this.handleDeleteColumn = handleDeleteColumn;
    this.allowCloneColumn = allowCloneColumn || false;
    this.cloneColumn = cloneColumn;
    this.allowReorderColumns = allowReorderColumns;
    this.matchCurrencyPadding = matchCurrencyPadding;
    this.linkCurrencyChips = linkCurrencyChips;
    this.showTotalColumn = showTotalColumn;
    this.showTitlesColumn = showTitlesColumn;
    this.showToolbar = showToolbar;
    this.showPreviousColsDivider = showPreviousColsDivider;
    this.showFirstRowAsTitle = showFirstRowAsTitle;
    this.allowConfirmAndDeleteColumn = allowConfirmAndDeleteColumn;
    this.afterCellChanged = afterCellChanged;
    this.conditions = conditions;
    this.tableData = tableData;
    this.rowConfig = rowConfig;
    this.columns = columns;
    this.name = name;
    this.disabled = disabled;
    this.currency = currency;
    this.initialCurrencyType = currency;
    this.ignoreCurrencyChangeNumber = ignoreCurrencyChangeNumber;
    this.hasColTitle = hasColTitle;
    this.parser = parser;
    this.xLength = rowConfig.length;
    this.yLength = columns.length;
    this.alphabet = alphabet ?? alphabetGenerator([], this.yLength);
    this.colTitleRow = colTitleRow;
    this.reverseParser = reverseParser;
    this.setDataSource = setDataSource;
    this.tableTerms = tableTerms;
    this.customTitles = customTitles;
    this.setColumns = setColumns;
    this.allowEmptyValues = allowEmptyValues;
    this.page = page;
    this.currencyFormatter = currencyFormatter;
    this.unitsFormatter = unitsFormatter;
    this.format = format;
    this.initialObj = initialObj;
    this.customValidations = customValidations;
    this.shouldValidate = shouldValidate;
    this.fieldAttributes = fieldAttributes;
    this.allowDeleteOnlySpecificColumn = allowDeleteOnlySpecificColumn;
    this.disableDeleteBtnOnFirstColumn = disableDeleteBtnOnFirstColumn;
    this.totalParser = totalParser;
    this.titleParser = titleParser;
    this.customDeleteConfirmation = customDeleteConfirmation;
    this.alwaysDisplayLegend = alwaysDisplayLegend;
    this.reverseParseAfterLoad = reverseParseAfterLoad;
    this.isReadOnly = isReadOnly;
    this.displayRowIndicator = displayRowIndicator;
    this.displayColumnIndicator = displayColumnIndicator;
    this.allowCopyColumn = allowCopyColumn;
    this.allowDeleteColumn = allowDeleteColumn;
    this.allowCopyRows = allowCopyRows;
    this.allowAddMultipleRows = allowAddMultipleRows;
    this.allowAddMultipleColumns = allowAddMultipleColumns;
    this.allowAddSingleRow = allowAddSingleRow;
    this.allowDeleteRow = allowDeleteRow;
    this.getColumns = getColumns;

    // Initialize properties required by IScalarSpreadsheetConfig
    this.className = '';
    this.children = React.createElement('div');
    this.collapsibleColumns = {};
    this.compareChanges = () => {
    };
    this.deleteColumn = () => {
    };
    this.deleteRowFn = () => {
    };
    this.disableVerticalScroll = false;
    this.displayDeleteRowBtn = false;
    this.formatDispatch = () => {
    };
    this.id = '';
    this.initialTableData = {};
    this.isLedgerTable = false;
    this.isToolbarVisible = false;
    this.notShowingQuarters = false;
    this.showPlaceholder = false;
    this.totalCells = {};
    this.validatedTitleCells = {};
  }

  async evaluate(): Promise<void> {
    this.cells = await this.parser({
      columns: this.columns,
      rowConfig: this.rowConfig,
      tableData: this.tableData,
      currencyCode: this.currency?.substring(0, 3) ?? '', // 'USD', 'EUR', 'GBP', etc.
      initialObject: this.initialObj,
    });

    if (this.showTotalColumn && this.totalParser) {
      this.totalCells = this.totalParser({
        columns: this.columns,
        rows: this.rowConfig,
        cells: this.cells,
      });
    }

    const titleRows = this.customTitles || this.rowConfig;
    this.titleCells = this.titleParser({
      columns: this.columns,
      rows: titleRows,
      cells: this.cells,
    });
    this.applyConditions({ [this.name ?? '']: this.cells });

    const [generatedTitles, generatedContent, generatedTotals] = this.generateData();
    this.titleData = generatedTitles;
    this.data = generatedContent;
    this.totalData = generatedTotals;
    this.generateCells([[this.titleData, 'TITLES'], [this.data], [this.totalData, 'TOTALS']]);
  }

  reverseShouldValidate(): void {
    this.shouldValidate = !this.shouldValidate;
  }

  setShouldValidate(shouldValidate: boolean): void {
    this.shouldValidate = shouldValidate;
  }

  applyChangeConditions(changes: any[], scope: any, onChange: (cell: any, value: any) => void): void {
    if (this.conditions) {
      changes.forEach(changeCell => {
        this.conditions?.({
          cell: changeCell,
          cells: scope,
          sheet: this,
          tableData: this.tableData,
          rowConfig: this.rowConfig,
          onCellsChanged: (cellChanges: CellChange[]) => {
            cellChanges.forEach(({ cell, value }) => {
              onChange(cell, value);
            });
          },
        });
      });
    }
  }

  applyConditions(scope: any): void {
    if (this.conditions && this.cells) {
      Object.values(this.cells as EvaluatedCellMap<CD, I, COLUMN, TD>)
        .filter(c => !c.totalKey)
        .forEach(cell  => {
          this.conditions?.({
            cell,
            cells: scope,
            sheet: this,
            tableData: this.tableData,
            rowConfig: this.rowConfig,
            // this is set to null as none of the cells within changes have a onChange function
            onCellsChanged: (_: any[]) => null,
          });
        });
    }
  }

  updateAllCellsDisplayConfig(): void {
    if (this.cells) {
      Object.values(this.cells).forEach(cell => {
        this.setCellDisplayConfig(cell);
      });
    }
  }

  setCellDisplayConfig(cell:  ConfigurationCell<CD, I, COLUMN, TD>): void {
    cell.setDisplayConfig?.(this.format, cell.customFormat);
    cell.dispatchEvent?.();
  }

  resetData(): void {
    if (this.data) {
      this.data = [...this.data];
    }
  }

  reverseParse(): any {
    if (this.reverseParser) {
      return this.reverseParser({
        titleCells: this.titleCells as EvaluatedCellMap<CD, I, COLUMN, TD>,
        cells: this.cells as EvaluatedCellMap<CD, I, COLUMN, TD>,
        columns: this.columns,
        allowEmptyValues: this.allowEmptyValues,
        rowConfig: this.rowConfig,
        tableData: this.tableData,
        fieldAttributes: this.fieldAttributes,
        initialObj: this.initialObj,
        sheet: this,
      });
    }
    return undefined;
  }
  ;

  generateCells(dataList: [(EvaluatedCell<CD, I, COLUMN, TD>[][] | string)[], EvaluatedCell<CD, I, COLUMN, TD>[][][], (EvaluatedCell<CD, I, COLUMN, TD>[][] | string)[]]): void {
    const soFar: EvaluatedCellMap<CD, I, COLUMN, TD> = {};
    const obj = this;

    const mapCellKey = (cells: EvaluatedCellMap<CD, I, COLUMN, TD>, cell: EvaluatedCell<CD, I, COLUMN, TD>, indexKey: string): void => {
      const objCells = obj.cells as unknown as EvaluatedCellMap<CD, I, COLUMN, TD> | undefined;
      // eslint-disable-next-line no-param-reassign
      cells[indexKey] = objCells?.[indexKey]?.evaluated ? objCells[cell.key as string] : cell;
      if (cell.isTitleCell && cell.key !== indexKey) {
        // eslint-disable-next-line no-param-reassign
        cell.key = indexKey;
      }
    };

    const generateKey = (keyExtension?: string, configKey?: string): string | undefined => {
      if (keyExtension && configKey) {
        return `${keyExtension}_${configKey}`;
      }
      return configKey;
    };

    dataList
      .filter(d => d)
      .forEach(dataItem => {
        const [data, keyExtension] = dataItem;
        (data as EvaluatedCell<CD, I, COLUMN, TD>[][]).forEach(row => {
          row.forEach(cell => {
            const key = generateKey(keyExtension as string, cell.key);
            if (key) {
              mapCellKey(soFar, cell, key);
              if (cell.customKey) {
                const customKey = (keyExtension ? `${keyExtension}_` : '') + cell.customKey;
                mapCellKey(soFar, cell, customKey);
              }
            }
          });
        });
      });
    this.cells = soFar;
  }

  async addColumns(index: number, columns: any[]): Promise<void> {
    this.columns.splice(index, 0, ...columns);
    this.yLength = this.columns.length;
    this.alphabet = alphabetGenerator([], this.yLength);
  }

  async addRows(index: number, rows: ICell<CD, I, COLUMN, TD>[]): Promise<void> {
    this.rowConfig.splice(index, 0, ...rows);
    this.xLength = this.rowConfig.length;
  }

  async reset({ rowConfig, columns, tableData }: ResetParams<COLUMN, TD, I, CD>): Promise<void> {
    if (rowConfig) {
      this.rowConfig = rowConfig;
      this.xLength = rowConfig.length;
    }

    if (columns) {
      this.columns = columns;
      this.yLength = this.columns.length;
      this.alphabet = alphabetGenerator([], this.yLength);
    }

    if (tableData) {
      this.tableData = tableData;
    }
  }

  deleteColumns(index: number, deleteCount: number): void {
    for (let i = index; i < deleteCount + index; i++) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.columns[i].is_deleted = true;
    }
    this.setColumns?.(this.columns);
    this.yLength = this.columns.length;
    this.alphabet = alphabetGenerator([], this.yLength);
  }

  generateCell({ cell, colIndex, rowIndex }: GenerateCellParams<I, COLUMN, TD, CD>): EvaluatedCell<CD, I, COLUMN, TD> {
    const inputCell = Object.assign(new Cell<CD, I, COLUMN, TD>(cell.expr, cell.key, rowIndex, colIndex, this, cell.rowNumber, cell.columnLegend), cell);

    if (rowIndex === 0 && (this.currencyFormatter || this.unitsFormatter)) {
      inputCell.className = cell.className ? `${cell.className} formatter-cell` : 'formatter-cell';
    }

    this.setCellDisplayConfig(inputCell);

    return inputCell;
  }

  refreshColumns(): void {
    const updatedColumns = this.getColumns();
    if (updatedColumns) {
      this.columns = updatedColumns;
    }
  }

  /**
   * Creates a cell object based on row and cell properties
   * @private
   */
  private createDataCell(row: ICell<CD, I, COLUMN, TD>, rowIndex: number, colIndex: number, cellKey: string, isColTitle: boolean): EvaluatedCell<CD, I, COLUMN, TD> {
    if (!this.cells) {
      logger.error('Cells should be initialized before generating data.', 'Spreadsheet Parsing error');
      throw new Error('Cells are not initialized');
    }

    // Create base cell with appropriate properties
    const cell = row.ignoreRowCopy
      ? {
        readOnly: true,
        ...(this.cells[cellKey] ? this.cells[cellKey] : {}),
      }
      : {
        readOnly: true,
        key: cellKey,
        ...row,
        ...(this.cells[cellKey] ? this.cells[cellKey] : {}),
      };

    // Apply read-only state
    if (this.isReadOnly) {
      cell.readOnly = true;
    }

    // Apply styling classes based on conditions
    if (this.hasColTitle && isColTitle) {
      cell.className = cell.className ? `${cell.className} table-header` : 'table-header';
    }

    if (row.largeHeader) {
      cell.className = cell.className ? `${cell.className} large-header` : 'large-header';
    }

    if (rowIndex === 0 && (this.currencyFormatter || this.unitsFormatter)) {
      cell.className = cell.className ? `${cell.className} formatter-cell` : 'formatter-cell';
    }

    // Handle special cases
    if (cell.hidden) {
      cell.value = null;
    }

    if (this.disabled) {
      cell.readOnly = true;
      if (cell.className) {
        cell.className = removeClassName(cell.className, 'read-only--white');
      }
    }

    if (row.ignoreRowCopy && row.isClassNameRequired) {
      const { className } = row;
      cell.className = cell.className ? `${cell.className} ${className}` : className;
    }

    if (cell.ignoreExpr) {
      cell.expr = undefined;
    }

    // DO NOT USE NULL COALESCING HERE - UNDER NO CIRCUMSTANCES
    cell.expr = cell.expr || cell.value;

    // Create the final cell object
    const inputCell = Object.assign(
      new Cell<CD, I, COLUMN, TD>(
        cell.expr, 
        cell.key || '', 
        rowIndex, 
        colIndex, 
        this, 
        cell.rowNumber ?? rowIndex + 1, 
        cell.columnLegend ?? ''
      ), 
      cell
    ) as EvaluatedCell<CD, I, COLUMN, TD>;

    // Handle component if present
    if (cell.component) {
      const newProps = { cell: inputCell, disabled: this.disabled };
      inputCell.component = React.cloneElement(cell.component, newProps);
    }

    this.setCellDisplayConfig(inputCell);

    return inputCell;
  }

  /**
   * Creates a total cell for a row if applicable
   * @private
   */
  private createTotalCell(rowIndex: number): EvaluatedCell<CD, I, COLUMN, TD> | null {
    if (!this.showTotalColumn || !this.totalCells || !Object.entries(this.totalCells)[rowIndex]) {
      return null;
    }

    const [totalCellKey, totalCell] = Object.entries(this.totalCells)[rowIndex];
    return this.generateCell({
      cell: { totalKey: totalCellKey, ...totalCell as ConfigurationCell<CD, I, COLUMN, TD>},
      rowIndex,
      colIndex: 0,
    });
  }

  /**
   * Creates a legend cell for row titles
   * @private
   */
  private createLegendCell(row: ICell<CD, I, COLUMN, TD>, rowIndex: number): EvaluatedCell<CD, I, COLUMN, TD> {
    let legendClassName = 'legend row-legend cell read-only';
    const xlsxRow = row as ICell & { isXlsxPreviewRow?: boolean };
    const rowLegendValue = getRowLegendValue(row, rowIndex);
    
    // Add large-header class if needed
    if (row.largeHeader && !this.showTitlesColumn) {
      legendClassName += ' large-header';
    }
    
    return this.generateCell({
      cell: {
        readOnly: true,
        className: legendClassName,
        forceComponent: false,
        expr: rowLegendValue.toString(),
        value: rowLegendValue,
        isLegend: true,
        valueViewer: null,
        isVisible: row.isVisible,
        hasSubRows: row.hasSubRows,
        showSubRows: row.showSubRows,
        ...(xlsxRow.isXlsxPreviewRow ? { style: getLegendRowLayoutStyle(row.style) } : {}),
        key: '',
        columnLegend: '',
        rowNumber: 0,
        alias: '',
        disabled: false,
      },
      rowIndex,
      colIndex: 0,
    });
  }

  /**
   * Creates a title cell with appropriate styling and properties
   * @private
   */
  private createTitleCell(
    titleCell: ConfigurationCell<CD, I, COLUMN, TD>, 
    titleCellKey: string, 
    row: ICell<CD, I, COLUMN, TD>,
    rowIndex: number, 
    isFirstRow: boolean
  ): EvaluatedCell<CD, I, COLUMN, TD> | null {
    if (!this.showTitlesColumn) {
      return null;
    }

    const isEditable = row.isEditableTitleCell;
    const classNames = ['row-label'];

    // Add appropriate class names based on conditions
    if (isFirstRow && this.showFirstRowAsTitle) {
      classNames.push('row-label-title');
    }

    if (row.className) {
      classNames.push(row.className);
    }

    if (isFirstRow && this.hasColTitle && this.showFirstRowAsTitle) {
      classNames.push('table-header');
    }

    if (isFirstRow && (this.currencyFormatter || this.unitsFormatter)) {
      classNames.push('formatter-cell');
    }

    if (titleCell.rowSpan && titleCell.rowSpan > 1) {
      classNames.push(`row-span-${titleCell.rowSpan}`);
    }

    // Create base title cell
    let cell = {
      ...titleCell,
      alias: isEditable ? row.alias : titleCellKey,
      linkedCellSymbols: isEditable ? row.linkedCellSymbols : undefined,
      expr: isEditable ? row.expr : '',
      key: titleCellKey,
      titleAlias: row.alias,
      className: classNames.join(' '),
      component: React.createElement(GridRowLabel, {
        cell: { ...titleCell, hasSubRows: row.hasSubRows, showSubRows: row.showSubRows } as ConfigurationCell<object, object, object, object>,
        currencyFormatter: isFirstRow && this.currencyFormatter,
        unitsFormatter: isFirstRow && this.unitsFormatter,
        formatterCell: isFirstRow,
      }),
      forceComponent: true,
      rowSpan: titleCell.rowSpan ?? 1,
      style: titleCell.style || {},
      isTitleCell: true,
    } as ConfigurationCell<CD, I, COLUMN, TD>;

    // Add title class name if present
    if (cell.titleClassName) {
      cell.className = `${cell.className} ${cell.titleClassName}`;
    }

    // Handle editable title cells
    if ((cell.isEditableTitleCell || cell.isTitleWithLabel) && row.alias) {
      const copyOfCell = { ...cell };
      classNames.push('editable');
      cell = {
        ...cell,
        key: row.alias,
        component: null,
        forceComponent: false,
        readOnly: cell.isTitleWithLabel,
        valueViewer: copyOfCell.valueViewer,
        dataEditor: copyOfCell.dataEditor,
        value: row.value,
        expr: row.value,
        isRequired: true,
        sheet: this,
        gridType: 'string' as GridType,
      };

      // Handle dropdown cells
      if (cell.useScalarSpreadsheetCell && cell.dropdown) {
        cell = Object.assign(
          new Cell(cell.expr, cell.key, rowIndex, 0, this, cell.rowNumber, cell.columnLegend), 
          {
            ...cell,
            className: `${cell.className} titles-column-selector`,
          }
        );
      }
    }

    return this.generateCell({ cell, rowIndex, colIndex: 0 });
  }

  getCellKey(colLegend: string, rowIndex: number): string {
    return colLegend + (rowIndex + 1);
  }

  /**
   * Generates data for the spreadsheet
   * @returns A tuple of [titleRows, updatedRows, totalRows]
   */
  generateData() {
    const titleRows: EvaluatedCell<CD, I, COLUMN, TD>[][] = [];
    const updatedRows: EvaluatedCell<CD, I, COLUMN, TD>[][] = [];
    const totalRows: EvaluatedCell<CD, I, COLUMN, TD>[][] = [];

    this.rowConfig.forEach((row, rowIndex) => {
      const isFirstRow = rowIndex === 0;
      const titleRow: EvaluatedCell<CD, I, COLUMN, TD>[] = [];
      const tableRow: EvaluatedCell<CD, I, COLUMN, TD>[] = [];
      const totalRow: EvaluatedCell<CD, I, COLUMN, TD>[] = [];
      const isColTitle = rowIndex === this.colTitleRow;

      // Generate data cells for each column
      for (let colIndex = 0; colIndex < this.yLength; colIndex += 1) {
        const colLegend = this.alphabet?.[colIndex] ?? '';
        const cellKey = this.getCellKey(colLegend, rowIndex);
        const inputCell = this.createDataCell(row, rowIndex, colIndex, cellKey, isColTitle);
        tableRow.push(inputCell);
      }

      // Generate total cell if needed
      const totalCell = this.createTotalCell(rowIndex);
      if (totalCell) {
        totalRow.push(totalCell);
      }

      // Generate title cells if needed
      if (this.titleCells && Object.entries(this.titleCells)[rowIndex]) {
        const [titleCellKey, titleCell] = Object.entries(this.titleCells)[rowIndex];
        
        // Create a legend cell
        const legendCell = this.createLegendCell(row, rowIndex);
        titleRow.push(legendCell);
        
        // Create a title cell
        const tableCell = this.createTitleCell(
          titleCell as ConfigurationCell<CD, I, COLUMN, TD>,
          titleCellKey,
          row,
          rowIndex,
          isFirstRow
        );
        
        if (tableCell) {
          titleRow.push(tableCell);
        }
      }

      titleRows.push(titleRow);
      updatedRows.push(tableRow);
      totalRows.push(totalRow);
    });

    mergeCells(titleRows as unknown as EvaluatedCell[][]);
    mergeCells(updatedRows as unknown as EvaluatedCell[][]);
    return [titleRows, updatedRows, totalRows];
  }

}
